/**
 * Incremental SHA-256 (FIPS 180-4). WebCrypto only hashes whole buffers, but transfers are
 * streamed chunk by chunk, so the digest is computed as bytes flow through.
 */

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
])

export class Sha256 {
  private h = new Uint32Array([0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19])
  private block = new Uint8Array(64)
  private blockLen = 0
  private total = 0
  private w = new Uint32Array(64)
  private finished = false

  update(data: Uint8Array): this {
    if (this.finished) throw new Error('digest already computed')
    let i = 0
    this.total += data.byteLength
    if (this.blockLen > 0) {
      const take = Math.min(64 - this.blockLen, data.byteLength)
      this.block.set(data.subarray(0, take), this.blockLen)
      this.blockLen += take
      i = take
      if (this.blockLen === 64) {
        this.compress(this.block, 0)
        this.blockLen = 0
      }
    }
    while (i + 64 <= data.byteLength) {
      this.compress(data, i)
      i += 64
    }
    if (i < data.byteLength) {
      this.block.set(data.subarray(i), 0)
      this.blockLen = data.byteLength - i
    }
    return this
  }

  digest(): Uint8Array {
    if (this.finished) throw new Error('digest already computed')
    this.finished = true
    const bitLen = BigInt(this.total) * 8n
    const pad = new Uint8Array(this.blockLen < 56 ? 64 - this.blockLen : 128 - this.blockLen)
    pad[0] = 0x80
    const dv = new DataView(pad.buffer)
    dv.setBigUint64(pad.byteLength - 8, bitLen, false)
    // feed the padding through the block machinery without touching `total`
    const total = this.total
    this.finished = false
    this.update(pad)
    this.total = total
    this.finished = true
    const out = new Uint8Array(32)
    const ov = new DataView(out.buffer)
    for (let i = 0; i < 8; i++) ov.setUint32(i * 4, this.h[i]!, false)
    return out
  }

  digestHex(): string {
    return toHex(this.digest())
  }

  private compress(data: Uint8Array, off: number) {
    const w = this.w
    for (let i = 0; i < 16; i++) {
      const j = off + i * 4
      w[i] = ((data[j]! << 24) | (data[j + 1]! << 16) | (data[j + 2]! << 8) | data[j + 3]!) >>> 0
    }
    for (let i = 16; i < 64; i++) {
      const x = w[i - 15]!
      const y = w[i - 2]!
      const s0 = (rotr(x, 7) ^ rotr(x, 18) ^ (x >>> 3)) >>> 0
      const s1 = (rotr(y, 17) ^ rotr(y, 19) ^ (y >>> 10)) >>> 0
      w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) >>> 0
    }
    let [a, b, c, d, e, f, g, h] = this.h as unknown as [number, number, number, number, number, number, number, number]
    for (let i = 0; i < 64; i++) {
      const S1 = (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) >>> 0
      const ch = ((e & f) ^ (~e & g)) >>> 0
      const t1 = (h + S1 + ch + K[i]! + w[i]!) >>> 0
      const S0 = (rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) >>> 0
      const maj = ((a & b) ^ (a & c) ^ (b & c)) >>> 0
      const t2 = (S0 + maj) >>> 0
      h = g
      g = f
      f = e
      e = (d + t1) >>> 0
      d = c
      c = b
      b = a
      a = (t1 + t2) >>> 0
    }
    const H = this.h
    H[0] = (H[0]! + a) >>> 0
    H[1] = (H[1]! + b) >>> 0
    H[2] = (H[2]! + c) >>> 0
    H[3] = (H[3]! + d) >>> 0
    H[4] = (H[4]! + e) >>> 0
    H[5] = (H[5]! + f) >>> 0
    H[6] = (H[6]! + g) >>> 0
    H[7] = (H[7]! + h) >>> 0
  }
}

function rotr(x: number, n: number): number {
  return ((x >>> n) | (x << (32 - n))) >>> 0
}

export function toHex(bytes: Uint8Array): string {
  let s = ''
  for (const b of bytes) s += b.toString(16).padStart(2, '0')
  return s
}

/** One-shot helper for small buffers. */
export function sha256Hex(data: Uint8Array): string {
  return new Sha256().update(data).digestHex()
}
